import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { Check, X, Users, Clock, CheckCircle2, FileText, Trash2, Download, ExternalLink, ShieldPlus } from "lucide-react";

const EXPORT_DATASETS = [
  { key: "lovers", label: "Lovers" },
  { key: "vendors", label: "Vendors" },
  { key: "opportunities", label: "Opportunities" },
  { key: "applications", label: "Applications" },
  { key: "users", label: "Users" },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("pending");
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [couples, setCouples] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportBusy, setExportBusy] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Admin invite modal
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [invitePw, setInvitePw] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteMsg, setInviteMsg] = useState("");
  const [inviteErr, setInviteErr] = useState("");
  const [admins, setAdmins] = useState([]);
  // Test email
  const [testTo, setTestTo] = useState("");
  const [testEvent, setTestEvent] = useState("password_reset");
  const [testBusy, setTestBusy] = useState("");
  const [testMsg, setTestMsg] = useState("");

  const sendTestEmail = async () => {
    if (!testTo || !testTo.includes("@")) { setTestMsg("Enter a valid email first."); return; }
    setTestBusy(testEvent); setTestMsg("");
    try {
      await api.post("/admin/test-email", { event: testEvent, to: testTo });
      setTestMsg(`Sent "${testEvent}" to ${testTo}. Check inbox in ~10 seconds.`);
    } catch (e) {
      setTestMsg(e?.response?.data?.detail || "Send failed.");
    } finally {
      setTestBusy("");
    }
  };

  const loadAdmins = async () => {
    try {
      const { data } = await api.get("/admin/admins");
      setAdmins(data);
    } catch {}
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    setInviteErr(""); setInviteMsg("");
    if (!inviteEmail || !inviteEmail.includes("@")) { setInviteErr("Provide a valid email."); return; }
    if (invitePw.length < 8) { setInviteErr("Password must be at least 8 characters."); return; }
    setInviteBusy(true);
    try {
      const { data } = await api.post("/admin/admins", {
        email: inviteEmail.trim().toLowerCase(),
        password: invitePw,
        name: inviteName.trim(),
      });
      setInviteMsg(data.promoted
        ? `${data.email} was promoted to admin.`
        : `${data.email} is now an admin.`);
      setInviteEmail(""); setInvitePw(""); setInviteName("");
      loadAdmins();
    } catch (e) {
      setInviteErr(e?.response?.data?.detail || "Couldn't create admin.");
    } finally {
      setInviteBusy(false);
    }
  };

  const openInvite = () => {
    setInviteErr(""); setInviteMsg("");
    setInviteOpen(true);
    loadAdmins();
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const { data: s } = await api.get("/admin/stats"); setStats(s);
      const { data: v } = await api.get("/admin/vendors", { params: { status: tab === "approved" ? "approved" : tab === "rejected" ? "rejected" : "pending" }});
      setVendors(v);
      if (tab === "couples") { const { data: c } = await api.get("/admin/couples"); setCouples(c); }
      if (tab === "opportunities") { const { data: o } = await api.get("/opportunities"); setOpportunities(o); }
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { refresh(); setSelected(new Set()); /* eslint-disable-next-line */ }, [tab]);

  const approve = async (id) => { await api.post(`/admin/vendors/${id}/approve`); refresh(); };
  const reject = async (id) => { await api.post(`/admin/vendors/${id}/reject`); refresh(); };
  const removeUser = async (id) => {
    if (!confirm("Remove this user and all their data?")) return;
    await api.delete(`/admin/users/${id}`); refresh();
  };
  const removeOpportunity = async (id, title) => {
    if (!confirm(`Delete the opportunity "${title}"? Any applications will be removed too.`)) return;
    try {
      await api.delete(`/opportunities/${id}`);
      refresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed.");
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allSelected = vendors.length > 0 && vendors.every((v) => selected.has(v.user_id));
  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(vendors.map((v) => v.user_id)));
  };

  const bulkAction = async (action) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const verb = action === "approve" ? "Approve" : "Reject";
    if (!confirm(`${verb} ${ids.length} vendor${ids.length === 1 ? "" : "s"}?`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/vendors/bulk", { user_ids: ids, action });
      setSelected(new Set());
      await refresh();
      if (data?.skipped?.length) {
        alert(`${data.processed.length} processed. ${data.skipped.length} skipped.`);
      }
    } catch (e) {
      alert(e?.response?.data?.detail || "Bulk action failed.");
    } finally {
      setBulkBusy(false);
    }
  };

  const showBulkBar = useMemo(() => selected.size > 0 && (tab === "pending" || tab === "rejected" || tab === "approved"), [selected.size, tab]);

  const downloadLoverCredentials = async () => {
    setExportBusy("credentials");
    try {
      const res = await api.get("/admin/exports/lover-credentials", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const disp = res.headers["content-disposition"] || "";
      const match = disp.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : "lover-credentials.csv";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert(e?.response?.data?.detail || "Download failed.");
    } finally {
      setExportBusy("");
    }
  };

  const doExport = async (dataset, format) => {
    const busyKey = `${dataset}-${format}`;
    setExportBusy(busyKey);
    try {
      const res = await api.get(`/admin/exports/${dataset}`, {
        params: { format }, responseType: "blob",
      });
      const blob = new Blob([res.data], { type: res.headers["content-type"] || (format === "csv" ? "text/csv" : "application/json") });
      const disp = res.headers["content-disposition"] || "";
      const match = disp.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `love-in-full_${dataset}.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert("Export failed. Check the console.");
      console.error(e);
    } finally {
      setExportBusy("");
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <p className="qld-overline mb-2">admin</p>
        <h1 className="font-serif text-4xl md:text-5xl text-ink mb-10">Mission control.</h1>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
          <StatCard icon={Users} label="Couples" value={stats?.couples_total ?? "—"} sub={`${stats?.couples_published ?? 0} published`} tone="lilac"/>
          <StatCard icon={Clock} label="Vendors pending" value={stats?.vendors_pending ?? "—"} sub={`${stats?.vendors_approved ?? 0} approved`} tone="mustard"/>
          <StatCard icon={CheckCircle2} label="Vendors total" value={stats?.vendors_total ?? "—"} sub="all-time" tone="powder"/>
          <StatCard icon={FileText} label="Opportunities" value={stats?.opportunities_total ?? "—"} sub={`${stats?.applications_total ?? 0} apps`} tone="linen"/>
        </div>

        <div className="lif-card-powder p-5 md:p-6 mb-10" data-testid="admin-exports">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div>
              <p className="qld-overline mb-1">data exports</p>
              <h2 className="font-serif text-xl text-ink leading-tight">Download a snapshot of the platform.</h2>
              <p className="text-xs text-black/65 mt-1">CSV for spreadsheets · JSON for backups or migrations.</p>
            </div>
            <Download className="w-5 h-5 text-black/70" strokeWidth={1.5}/>
          </div>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {EXPORT_DATASETS.map((d) => (
              <div key={d.key} className="lif-card bg-linen p-3 flex flex-col gap-2" data-testid={`admin-export-${d.key}`}>
                <p className="text-sm font-medium text-ink">{d.label}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => doExport(d.key, "csv")}
                    disabled={!!exportBusy}
                    data-testid={`admin-export-${d.key}-csv`}
                    className="flex-1 lif-btn lif-btn-mustard !py-1.5 !px-3 text-xs"
                  >
                    {exportBusy === `${d.key}-csv` ? "…" : "CSV"}
                  </button>
                  <button
                    onClick={() => doExport(d.key, "json")}
                    disabled={!!exportBusy}
                    data-testid={`admin-export-${d.key}-json`}
                    className="flex-1 lif-btn lif-btn-lilac !py-1.5 !px-3 text-xs"
                  >
                    {exportBusy === `${d.key}-json` ? "…" : "JSON"}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t-2 border-black/15">
            <button
              onClick={downloadLoverCredentials}
              disabled={!!exportBusy}
              data-testid="admin-download-credentials"
              className="lif-btn lif-btn-wine !px-5 !py-2.5 text-sm"
            >
              <Download className="w-4 h-4" strokeWidth={1.5} />
              {exportBusy === "credentials" ? "Preparing…" : "Download lover temp-passwords (.csv)"}
            </button>
            <p className="text-[11px] text-black/55 mt-2 leading-snug max-w-md">
              Mail-merge from this CSV for your outreach. <strong>Treat as confidential</strong> — it contains plaintext logins. Re-run the password-reset script to rotate all temps.
            </p>
          </div>
        </div>

        {/* Admin team panel */}
        <div className="lif-card-lilac p-5 md:p-6 mb-10" data-testid="admin-team-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="qld-overline mb-1">admin team</p>
              <h2 className="font-serif text-xl text-ink leading-tight">Invite another admin.</h2>
              <p className="text-xs text-black/65 mt-1">Anyone with admin access has the keys to the kingdom — only invite people you trust.</p>
            </div>
            <button
              onClick={openInvite}
              data-testid="admin-invite-open"
              className="lif-btn lif-btn-wine !px-5 !py-2.5 text-sm"
            >
              <ShieldPlus className="w-4 h-4" strokeWidth={1.5}/>
              Invite admin
            </button>
          </div>
        </div>

        {/* Test transactional email panel */}
        <div className="lif-card-mustard p-5 md:p-6 mb-10" data-testid="admin-test-email-panel">
          <p className="qld-overline mb-1">test transactional emails</p>
          <h2 className="font-serif text-xl text-ink leading-tight mb-1">Send a test email to any address.</h2>
          <p className="text-xs text-black/65 mb-4">Pick one of the 6 lifecycle events — we'll fire it via Resend with sample data so you can preview the formatting and deliverability.</p>

          <div className="flex flex-col md:flex-row gap-3">
            <select
              value={testEvent}
              onChange={(e) => setTestEvent(e.target.value)}
              data-testid="admin-test-email-event"
              className="qld-input md:flex-1"
            >
              <option value="new_couple_signup">new_couple_signup - Welcome lover</option>
              <option value="new_vendor_signup">new_vendor_signup - Application received</option>
              <option value="vendor_approved">vendor_approved - You're in</option>
              <option value="new_opportunity_posted">new_opportunity_posted - Opp live</option>
              <option value="couple_applied_to_opportunity">couple_applied_to_opportunity - New application</option>
              <option value="password_reset">password_reset - Reset link</option>
              <option value="profile_incomplete">profile_incomplete - 7-day nudge</option>
              <option value="profile_deleted">profile_deleted - Farewell</option>
            </select>
            <input
              type="email"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="you@example.com"
              data-testid="admin-test-email-to"
              className="qld-input md:flex-1"
            />
            <button
              onClick={sendTestEmail}
              disabled={!!testBusy}
              data-testid="admin-test-email-send"
              className="lif-btn lif-btn-wine !px-5 !py-2.5 text-sm shrink-0"
            >
              {testBusy ? "Sending…" : "Send test"}
            </button>
          </div>
          {testMsg && (
            <p className="text-xs mt-3 px-3 py-2 rounded-2xl bg-linen border-2 border-black inline-block" data-testid="admin-test-email-msg">
              {testMsg}
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { id: "pending", label: "Pending vendors" },
            { id: "approved", label: "Approved vendors" },
            { id: "rejected", label: "Rejected" },
            { id: "couples", label: "All lovers" },
            { id: "opportunities", label: "All opportunities" },
          ].map((t) => (
            <button key={t.id} onClick={()=>setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`lif-toggle ${tab===t.id ? "lif-toggle-active" : "hover:bg-mustard"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-muted">Loading…</p>
        ) : tab === "opportunities" ? (
          <div className="space-y-3" data-testid="admin-opp-list">
            {opportunities.length === 0 ? (
              <div className="qld-card p-10 text-center text-muted">No opportunities yet.</div>
            ) : opportunities.map((o) => (
              <div key={o.opp_id} className="qld-card p-5 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid={`admin-opp-${o.opp_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-xl text-ink">{o.title}</p>
                  <p className="text-sm text-muted truncate">
                    {o.vendor_name || "(unknown vendor)"} · {o.type?.replace("_", " ") || "—"}
                    {o.location_city && <> · {[o.location_city, o.location_country].filter(Boolean).join(", ")}</>}
                    {o.is_active === false && <> · <span className="text-wine">closed</span></>}
                  </p>
                  {o.description && (
                    <p className="text-xs text-black/60 mt-1 line-clamp-2">{o.description}</p>
                  )}
                </div>
                <div className="flex gap-2 items-center shrink-0">
                  <a
                    href="/opportunities"
                    className="text-xs text-wine font-medium hover:underline inline-flex items-center gap-1"
                    title="View on public board"
                  >
                    <ExternalLink className="w-3 h-3" strokeWidth={1.5}/> View
                  </a>
                  <button
                    onClick={() => removeOpportunity(o.opp_id, o.title)}
                    data-testid={`admin-opp-delete-${o.opp_id}`}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full border-2 border-black bg-linen hover:bg-wine hover:text-white transition"
                    title="Delete opportunity"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5}/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : tab === "couples" ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {couples.length === 0 && <p className="text-muted">No lover profiles yet.</p>}
            {couples.map((l) => (
              <div key={l.user_id} className="qld-card p-5 flex flex-col gap-2" data-testid={`admin-couple-${l.user_id}`}>
                <p className="font-serif text-xl text-ink leading-tight">{l.display_name}</p>
                <p className="text-xs text-muted truncate">{l.contact_email}</p>
                <p className="text-xs text-muted">{l.is_published ? "Published" : "Hidden"}{l.anonymous_display ? " · Anonymous" : ""}</p>
                <div className="flex items-center justify-between mt-2 pt-2 border-t-2 border-black/10">
                  <a
                    href={`/couples/${l.user_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-wine font-medium hover:underline inline-flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" strokeWidth={1.5}/> View
                  </a>
                  <button
                    onClick={()=>removeUser(l.user_id)}
                    data-testid={`admin-couple-delete-${l.user_id}`}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-full border-2 border-black bg-linen hover:bg-wine hover:text-white transition"
                    title="Delete this lover"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5}/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-vendor-list">
            {vendors.length === 0 ? (
              <div className="qld-card p-10 text-center text-muted">No vendors in this bucket.</div>
            ) : (
              <>
                <div className="flex items-center justify-between px-2 mb-2">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <span
                      className={`w-5 h-5 rounded border-2 border-black flex items-center justify-center transition-all
                        ${allSelected ? "bg-wine" : "bg-linen hover:bg-mustard"}`}
                    >
                      {allSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                    </span>
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      data-testid="admin-vendor-select-all"
                    />
                    <span className="text-xs uppercase tracking-[0.18em] font-semibold text-black/70">
                      {allSelected ? "Deselect all" : "Select all"}
                    </span>
                  </label>
                  <p className="text-xs text-black/60" data-testid="admin-vendor-selected-count">
                    {selected.size > 0 ? `${selected.size} selected` : `${vendors.length} total`}
                  </p>
                </div>
                {vendors.map((v) => {
                  const isChecked = selected.has(v.user_id);
                  return (
                    <div
                      key={v.user_id}
                      className={`qld-card p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 transition ${isChecked ? "bg-mustard/30" : ""}`}
                    >
                      <label className="flex items-start gap-3 flex-1 cursor-pointer">
                        <span
                          className={`mt-1 w-5 h-5 rounded border-2 border-black flex items-center justify-center transition-all shrink-0
                            ${isChecked ? "bg-wine" : "bg-linen hover:bg-mustard"}`}
                        >
                          {isChecked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                        </span>
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={isChecked}
                          onChange={() => toggleSelect(v.user_id)}
                          data-testid={`admin-vendor-check-${v.user_id}`}
                        />
                        <div className="flex-1">
                          <p className="font-serif text-xl text-ink">{v.name}</p>
                          <p className="text-sm text-muted">{v.email}</p>
                          {v.vendor_profile && (
                            <p className="text-xs text-muted mt-1">
                              {v.vendor_profile.vendor_type || "—"} · {[v.vendor_profile.location_city, v.vendor_profile.location_country].filter(Boolean).join(", ") || "—"}
                              {v.vendor_profile.portfolio_url && <> · <a href={v.vendor_profile.portfolio_url} target="_blank" rel="noreferrer" className="qld-link">portfolio</a></>}
                              {v.vendor_profile.website && <> · <a href={v.vendor_profile.website} target="_blank" rel="noreferrer" className="qld-link">site</a></>}
                            </p>
                          )}
                        </div>
                      </label>
                      <div className="flex gap-2 flex-wrap">
                        {tab !== "approved" && (
                          <button onClick={()=>approve(v.user_id)} data-testid={`admin-approve-${v.user_id}`}
                            className="lif-btn lif-btn-mustard !px-4 !py-2 text-sm">
                            <Check className="w-4 h-4" strokeWidth={1.5}/> Approve
                          </button>
                        )}
                        {tab !== "rejected" && (
                          <button onClick={()=>reject(v.user_id)} data-testid={`admin-reject-${v.user_id}`}
                            className="lif-btn lif-btn-lilac !px-4 !py-2 text-sm">
                            <X className="w-4 h-4" strokeWidth={1.5}/> Reject
                          </button>
                        )}
                        <button onClick={()=>removeUser(v.user_id)} title="Delete user" data-testid={`admin-delete-${v.user_id}`}
                          className="inline-flex items-center justify-center w-9 h-9 rounded-full border-2 border-black bg-linen hover:bg-wine hover:text-white transition">
                          <Trash2 className="w-4 h-4" strokeWidth={1.5}/>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>

      {/* Sticky bulk-action bar */}
      {showBulkBar && (
        <div
          className="fixed left-1/2 -translate-x-1/2 bottom-6 z-40 bg-linen border-2 border-black rounded-full px-5 py-3 flex items-center gap-3 shadow-2xl animate-fade-up"
          data-testid="admin-bulk-bar"
        >
          <span className="text-sm font-medium text-ink" data-testid="admin-bulk-count">
            {selected.size} selected
          </span>
          <div className="h-5 w-px bg-black/20 mx-1" />
          {tab !== "approved" && (
            <button
              onClick={() => bulkAction("approve")}
              disabled={bulkBusy}
              data-testid="admin-bulk-approve"
              className="lif-btn lif-btn-mustard !px-4 !py-2 text-sm"
            >
              <Check className="w-4 h-4" strokeWidth={1.5} /> Approve
            </button>
          )}
          {tab !== "rejected" && (
            <button
              onClick={() => bulkAction("reject")}
              disabled={bulkBusy}
              data-testid="admin-bulk-reject"
              className="lif-btn lif-btn-lilac !px-4 !py-2 text-sm"
            >
              <X className="w-4 h-4" strokeWidth={1.5} /> Reject
            </button>
          )}
          <button
            onClick={() => setSelected(new Set())}
            disabled={bulkBusy}
            data-testid="admin-bulk-clear"
            className="text-xs text-wine font-medium hover:underline px-2"
          >
            Clear
          </button>
        </div>
      )}

      {/* Invite admin modal */}
      {inviteOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-8 overflow-y-auto"
          onClick={() => setInviteOpen(false)}
          data-testid="admin-invite-modal"
        >
          <div
            className="bg-linen border-2 border-black rounded-[1.5rem] w-full max-w-lg p-7 md:p-9 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setInviteOpen(false)}
              className="absolute top-4 right-4 w-9 h-9 rounded-full border-2 border-black bg-linen hover:bg-wine hover:text-white transition flex items-center justify-center"
              aria-label="Close"
              data-testid="admin-invite-close"
            >
              <X className="w-4 h-4" strokeWidth={1.5}/>
            </button>

            <p className="qld-overline mb-2">grant admin access</p>
            <h3 className="font-serif text-3xl text-ink mb-2 leading-tight">Invite a new admin</h3>
            <p className="text-sm text-black/70 mb-6">
              They'll have full access to Mission Control — approve vendors, delete lovers, post opportunities and read all data. Use carefully.
            </p>

            <form onSubmit={submitInvite} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">Name (optional)</label>
                <input
                  data-testid="admin-invite-name"
                  type="text"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  className="qld-input"
                  placeholder="What should we call them?"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">Email</label>
                <input
                  data-testid="admin-invite-email"
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="qld-input"
                  placeholder="them@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">Temporary password</label>
                <input
                  data-testid="admin-invite-pw"
                  type="text"
                  required
                  minLength={8}
                  value={invitePw}
                  onChange={(e) => setInvitePw(e.target.value)}
                  className="qld-input font-mono"
                  placeholder="At least 8 characters"
                />
                <p className="text-xs text-black/55 mt-1">
                  Share this with them via a secure channel. They can change it from <code>/forgot-password</code> after logging in.
                </p>
              </div>

              {inviteErr && <p className="text-sm text-wine" data-testid="admin-invite-error">{inviteErr}</p>}
              {inviteMsg && (
                <p className="text-sm bg-mustard border-2 border-black rounded-2xl px-4 py-2.5" data-testid="admin-invite-success">
                  {inviteMsg}
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setInviteOpen(false)}
                  disabled={inviteBusy}
                  className="lif-btn lif-btn-cream sm:order-1"
                  data-testid="admin-invite-cancel"
                >
                  Done
                </button>
                <button
                  type="submit"
                  disabled={inviteBusy}
                  className="lif-btn lif-btn-wine flex-1 sm:order-2"
                  data-testid="admin-invite-submit"
                >
                  {inviteBusy ? "Creating…" : <>Create admin <ShieldPlus className="w-4 h-4" strokeWidth={1.5}/></>}
                </button>
              </div>
            </form>

            {/* Current admins */}
            {admins.length > 0 && (
              <div className="mt-7 pt-6 border-t-2 border-black/15">
                <p className="qld-overline mb-3">current admins ({admins.length})</p>
                <ul className="space-y-2" data-testid="admin-invite-list">
                  {admins.map((a) => (
                    <li key={a.user_id} className="text-sm flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-ink truncate">{a.name || a.email.split("@")[0]}</p>
                        <p className="text-xs text-black/60 truncate">{a.email}</p>
                      </div>
                      <span className="text-[10px] uppercase tracking-wider text-black/50 shrink-0">{a.auth_provider || "password"}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}

function StatCard({ icon: Icon, label, value, sub, tone }) {
  const toneMap = {
    lilac:   "bg-lilac",
    mustard: "bg-mustard",
    powder:  "bg-powder",
    linen:   "bg-linen",
  };
  return (
    <div className={`rounded-[1.5rem] border-2 border-black p-5 ${toneMap[tone] || "bg-linen"}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.2em] text-black/70">{label}</p>
        <Icon className="w-4 h-4 text-black/70" strokeWidth={1.5}/>
      </div>
      <p className="font-serif text-4xl text-ink mt-2">{value}</p>
      <p className="text-xs text-black/60 mt-1">{sub}</p>
    </div>
  );
}
